import { Tooltip } from 'antd';
import clsx from 'clsx';
import { NavLink } from 'react-router';
import { useCurrentUser } from '../../shared/features/session/components/CurrentUserProvider';
import { useComplianceAttentionCount } from '../../features/compliance/public/use-compliance-attention-count';
import { OFFICE_SECTION_GROUPS, OFFICE_SECTIONS, type OfficeSection } from '../../shared/components/office-sections';
import { visibleSections } from '../policies/visible-sections';

interface SidebarNavProps {
  collapsed: boolean;
  /** Taller rows for fingers, in the phone drawer. */
  touch?: boolean;
}

function CountBadge({ count, collapsed }: { count: number; collapsed: boolean }) {
  return (
    <span
      aria-label={`${count} need attention`}
      className={clsx(
        'flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] leading-none font-medium text-white',
        collapsed ? 'absolute top-0.5 right-0.5' : 'ml-auto',
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarLink({ section, collapsed, touch, badgeCount }: SidebarNavProps & { section: OfficeSection; badgeCount?: number | null }) {
  const { slug, label, icon: Icon } = section;
  const hasBadge = badgeCount != null && badgeCount > 0;
  const collapsedLabel = hasBadge ? `${label}, ${badgeCount} need attention` : label;
  const link = (
    <NavLink
      to={`/${slug}`}
      aria-label={collapsed ? collapsedLabel : undefined}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-2.5 rounded-md transition-colors',
          touch ? 'h-10 text-sm' : 'h-8 text-[13px]',
          collapsed ? 'justify-center' : 'px-2.5',
          isActive ? 'bg-sider-selected font-medium text-sider-selected-fg' : 'text-muted hover:bg-sider-selected/60 hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={clsx('size-4 shrink-0', isActive ? 'text-primary' : 'text-muted group-hover:text-fg')} strokeWidth={isActive ? 2.25 : 1.75} />
          {!collapsed && <span className="truncate">{label}</span>}
          {hasBadge && <CountBadge count={badgeCount} collapsed={collapsed} />}
        </>
      )}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

export function SidebarNav({ collapsed, touch = false }: SidebarNavProps) {
  const { state } = useCurrentUser();
  const permissions = state.status === 'authenticated' ? state.user.permissions : [];
  const allowedSections = visibleSections(OFFICE_SECTIONS, permissions);

  const complianceCount = useComplianceAttentionCount(allowedSections.some((section) => section.slug === 'compliance'));

  return (
    <nav aria-label="Office" className="flex flex-col gap-5 px-2.5">
      {OFFICE_SECTION_GROUPS.map((group) => {
        const sections = allowedSections.filter((s) => s.group === group);
        if (sections.length === 0) return null;

        return (
          <div key={group} className="flex flex-col gap-0.5">
            {collapsed ? (
              <div aria-hidden className="mx-auto mb-1.5 h-px w-5 bg-line" />
            ) : (
              <p className="mb-1 px-2.5 text-[11px] font-medium tracking-wider text-muted uppercase">{group}</p>
            )}
            {sections.map((section) => (
              <SidebarLink
                key={section.slug}
                section={section}
                collapsed={collapsed}
                touch={touch}
                badgeCount={section.slug === 'compliance' ? complianceCount : null}
              />
            ))}
          </div>
        );
      })}
    </nav>
  );
}
